// Server helpers must not initialize browser analytics through the package barrel.

import { networkStatusCode } from '@twisto/api-common/network'

export const FETCH_TIMEOUT_MS = 3000

export class CmsUnavailableError extends Error {
  constructor() {
    super('CMS is temporarily unavailable')
    this.name = 'CmsUnavailableError'
  }
}

export class CmsClientError extends Error {
  constructor(
    readonly status: number,
    readonly retryAfter?: string
  ) {
    super('CMS request rejected')
    this.name = 'CmsClientError'
  }
}

export class CmsNotFoundError extends CmsClientError {
  constructor() {
    super(networkStatusCode.notFound)
    this.message = 'CMS resource not found'
    this.name = 'CmsNotFoundError'
  }
}

export const isCmsClientError = (error: unknown): error is CmsClientError =>
  error instanceof Error &&
  (error.name === 'CmsClientError' || error.name === 'CmsNotFoundError')

export const isCmsNotFoundError = (error: unknown): error is Error =>
  error instanceof Error && error.name === 'CmsNotFoundError'

// Pending cache fills are shared across route bundles with separate class identities.
export const isCmsUnavailableError = (error: unknown): error is Error =>
  error instanceof Error && error.name === 'CmsUnavailableError'

export const waitForSignal = <T>(
  promise: Promise<T>,
  signal?: AbortSignal
): Promise<T> => {
  if (!signal) {
    return promise
  }

  return new Promise((resolve, reject) => {
    const abort = () => reject(new CmsUnavailableError())
    signal.addEventListener('abort', abort, { once: true })
    if (signal.aborted) {
      abort()
    }
    promise.then(resolve, reject).finally(() => {
      signal.removeEventListener('abort', abort)
    })
  })
}

export const fetchJson = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const controller = new AbortController()
  const abort = () => controller.abort()
  const timer = setTimeout(abort, FETCH_TIMEOUT_MS)
  options.signal?.addEventListener('abort', abort, { once: true })
  if (options.signal?.aborted) {
    abort()
  }

  try {
    if (controller.signal.aborted) {
      throw new CmsUnavailableError()
    }

    return await waitForSignal(
      (async () => {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        })
        if (response.status === networkStatusCode.notFound) {
          throw new CmsNotFoundError()
        }
        if (
          response.status >= networkStatusCode.badRequest &&
          response.status < networkStatusCode.internalServerError
        ) {
          throw new CmsClientError(
            response.status,
            response.headers.get('Retry-After') ?? undefined
          )
        }
        if (!response.ok) {
          throw new CmsUnavailableError()
        }

        return response.json()
      })(),
      controller.signal
    )
  } catch (error) {
    if (isCmsClientError(error)) {
      throw error
    }
    throw new CmsUnavailableError()
  } finally {
    controller.abort()
    clearTimeout(timer)
    options.signal?.removeEventListener('abort', abort)
  }
}
